import { FocusScope } from '@radix-ui/react-focus-scope';
import { useRouterState } from '@tanstack/react-router';
import { observer } from 'mobx-react-lite';
import { useEffect, useRef, useState } from 'react';

import { AgentsEffects } from '@/features/agents/agents-effects';
import { NavRail } from '@/features/nav-rail/nav-rail';
import { NavRailPanel } from '@/features/nav-rail/nav-rail-panel';
import { useNavRailPanel } from '@/features/nav-rail/nav-rail.hooks';
import { useTabSetup } from '@/features/tabs';
import { useHotkeys } from '@/hooks/hotkeys/useHotkeys';
import { useSetupUserAndInitializeChatPolling } from '@/hooks/useSetupUserAndChatPolling';
import { withUIDelay } from '@/lib/animate';
import { cn } from '@/lib/utils';
import {
  DrawerNavigationProvider,
  useDrawerNavigation,
} from '@/providers/DrawerNavigationProvider';
import { SIDEBAR_MIN_WIDTH, uiStore } from '@/stores/ui.store';
import { inBrowser } from '@/utils';

import { YStack } from '../../ui/layout/layout';
import { ResizableBox, type ResizableBoxHandle } from '../../ui/ResizableBox';
import MainLayout from '../MainLayout/MainLayout';

import { useSidebarListsLoaded } from './Layout.state';
import { Navbar } from './Navbar';

export const Main = () => {
  useSetupUserAndInitializeChatPolling();

  useTabSetup();
  useTrackNarrowViewport();

  return (
    <DrawerNavigationProvider>
      <YStack className="shell-wash relative h-full">
        <AgentsEffects />
        <SidebarLayout />
      </YStack>
    </DrawerNavigationProvider>
  );
};

const SidebarLayout = observer(() => {
  const panel = useNavRailPanel();
  const narrow = uiStore.isNarrowViewport;
  useSidebarListsLoaded();
  useHotkeys();

  return (
    <>
      <Navbar />
      <div className="flex flex-1 min-h-0 relative">
        <NavRail />
        <div
          className={cn(
            'relative mb-2 mr-2 flex min-w-0 flex-1 overflow-hidden rounded-2xl border border-border-subtle shell-frame',
            inBrowser() && 'mt-2'
          )}
        >
          {panel && !narrow && (
            <SidebarPanel>
              <NavRailPanel panel={panel} />
            </SidebarPanel>
          )}
          {panel && narrow && (
            <PanelDrawer>
              <NavRailPanel panel={panel} />
            </PanelDrawer>
          )}
          <ContentPane />
        </div>
      </div>
    </>
  );
});

const useTrackNarrowViewport = () => {
  useEffect(() => {
    const mql = window.matchMedia('(max-width: 767px)');
    const sync = () => uiStore.setIsNarrowViewport(mql.matches);
    sync();
    mql.addEventListener('change', sync);
    return () => mql.removeEventListener('change', sync);
  }, []);
};

const PanelDrawer = ({ children }: { children: React.ReactNode }) => {
  const { isOpen, setIsOpen } = useDrawerNavigation();
  const pathname = useRouterState({ select: (s) => s.location.pathname });

  useEffect(() => setIsOpen(false), [pathname, setIsOpen]);
  useEffect(() => () => setIsOpen(false), [setIsOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setIsOpen(false);
    };
    document.addEventListener('keydown', closeOnEscape);
    return () => document.removeEventListener('keydown', closeOnEscape);
  }, [isOpen, setIsOpen]);

  if (!isOpen) return null;

  return (
    <>
      <div
        aria-hidden
        className="absolute inset-0 z-30 bg-black/30"
        onClick={() => setIsOpen(false)}
      />
      <FocusScope trapped loop asChild>
        <div
          role="dialog"
          aria-modal="true"
          aria-label="Navigation"
          className="absolute inset-y-0 left-0 z-40 flex w-[280px] max-w-[85%] bg-background shadow-xl"
        >
          {children}
        </div>
      </FocusScope>
    </>
  );
};

const ContentPane = () => (
  <div className="relative min-w-0 flex-1">
    <MainLayout />
  </div>
);

const SidebarPanel = observer(({ children }: { children: React.ReactNode }) => {
  const panelRef = useRef<ResizableBoxHandle>(null);
  const observerRef = useRef<ResizeObserver | null>(null);
  const isCollapsed = uiStore.sidebarCollapsed;
  const [isReady, setIsReady] = useState(false);

  useEffect(() => {
    if (!isReady || !panelRef.current) return;

    if (isCollapsed) {
      panelRef.current.collapse();
    } else {
      panelRef.current.expand();
    }
  }, [isCollapsed, isReady]);

  useEffect(() => withUIDelay(() => setIsReady(true)), []);

  // on a reload that starts collapsed the panel's children never mount, so
  // the ResizeObserver never reports and the width sits at its 240 default
  useEffect(() => {
    if (panelRef.current?.isCollapsed()) uiStore.setSidebarWidth(0);
  }, []);

  // Track the sidebar's width for uiStore.sidebarWidth readers. Content
  // keeps its resting width while the box animates, so this fires on mount
  // and drags, not per frame. Callback ref re-attaches across un/remounts.
  const attachSidebarObserver = (el: HTMLDivElement | null) => {
    observerRef.current?.disconnect();
    observerRef.current = null;
    if (!el) return;
    const observer = new ResizeObserver(([entry]) => {
      uiStore.setSidebarWidth(entry.contentRect.width);
    });
    observer.observe(el);
    observerRef.current = observer;
  };

  return (
    <ResizableBox
      ref={panelRef}
      id="sidebar"
      initialWidth={240}
      minWidth={SIDEBAR_MIN_WIDTH}
      maxWidth={500}
      className="hidden md:flex"
      snapCollapse
      // toggles start in uiStore; a drag-snap is the only collapse the box
      // starts itself, so it is the only change reported back
      onDragCollapse={() => uiStore.setSidebarCollapsed(true)}
      // the ResizeObserver misses the final collapsed frame (its target
      // unmounts), so pin the tracked width to the endpoint
      onResize={(width) => uiStore.setSidebarWidth(width)}
      // extend the resize handle up through the navbar row so the sidebar
      // edge is draggable there too
      handleClassName="shell absolute right-0 -top-11 bottom-0"
      handleIndicatorClassName="top-11 bottom-0"
    >
      <div ref={attachSidebarObserver} className="relative flex h-full w-full">
        {children}
      </div>
    </ResizableBox>
  );
});
